import { apiService } from '@/services/apiService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { showError, showSuccess } from '@/services/toastService'
import { useAuthStore } from '@/stores/authStore'
import { useEventStore } from '@/stores/eventStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { EventType, type Event } from '@/types/event'
import type { WorkspacePOI } from '@/types/poi'
import type { Tour, TourListItem } from '@/types/tour'

let pinnedWorkspaceCache: { userId: number; clientId: string } | null = null

type PinnableTour = Pick<Tour, 'clientId' | 'name' | 'isPinned'> | TourListItem
type PinTourOptions = {
  preserveDetailSelection?: boolean
  suppressToast?: boolean
}
export type PinnableCollection = {
  clientId?: string
  tours: PinnableTour[]
  pois?: WorkspacePOI[] | null
}
export type UnpinAllPinnedItemsResult = {
  unpinnedCount: number
  failedCount: number
}

export const canUnpinPOIFromPinned = (poi: WorkspacePOI, pinnedWorkspaceId: string) =>
  poi.sourceWorkspaceClientId !== pinnedWorkspaceId

type RemovedTourState = {
  tour: Tour
  index: number
}
type PinnedMembershipKind = 'tour' | 'poi'

const pendingToggleOperations = new Map<string, Promise<unknown>>()

const runSerializedToggle = async <T>(key: string, operation: () => Promise<T>): Promise<T> => {
  const previous = pendingToggleOperations.get(key) ?? Promise.resolve()
  const current = previous.catch(() => undefined).then(operation)
  pendingToggleOperations.set(key, current)

  try {
    return await current
  } finally {
    if (pendingToggleOperations.get(key) === current) {
      pendingToggleOperations.delete(key)
    }
  }
}

const setTourPinnedState = (tour: PinnableTour, isPinned: boolean) => {
  ;(tour as PinnableTour & { isPinned?: boolean }).isPinned = isPinned
}

const setPOIPinnedState = (poi: WorkspacePOI, isPinned: boolean) => {
  poi.isPinned = isPinned
}

const isTour = (tour: PinnableTour): tour is Tour => Array.isArray((tour as Tour).tracks)

const isActivePinnedWorkspace = (pinnedWorkspaceId: string | null = null) => {
  const workspaceStore = useWorkspaceStore()
  return (
    (workspaceStore.isSystem === true && workspaceStore.systemKey === 'pinned') ||
    (pinnedWorkspaceId !== null && workspaceStore.clientId === pinnedWorkspaceId)
  )
}

const addTourToActivePinnedWorkspace = (tour: PinnableTour, pinnedWorkspaceId: string) => {
  const workspaceStore = useWorkspaceStore()
  if (!isActivePinnedWorkspace(pinnedWorkspaceId) || !isTour(tour)) return
  if (workspaceStore.tours.some((candidate) => candidate.clientId === tour.clientId)) return

  workspaceStore.tours.push(tour)
}

const restoreTourToActivePinnedWorkspace = (
  removedState: RemovedTourState | null,
  pinnedWorkspaceId: string | null,
) => {
  if (!removedState || !isActivePinnedWorkspace(pinnedWorkspaceId)) return
  const workspaceStore = useWorkspaceStore()
  if (workspaceStore.tours.some((candidate) => candidate.clientId === removedState.tour.clientId)) {
    return
  }
  workspaceStore.tours.splice(removedState.index, 0, removedState.tour)
}

const removeTourFromActivePinnedWorkspace = (
  tourClientId: string,
  pinnedWorkspaceId: string | null,
  options: PinTourOptions = {},
): RemovedTourState | null => {
  const workspaceStore = useWorkspaceStore()
  if (!isActivePinnedWorkspace(pinnedWorkspaceId)) return null

  const tourIndex = workspaceStore.tours.findIndex((tour) => tour.clientId === tourClientId)
  if (tourIndex === -1) return null

  const [removedTour] = workspaceStore.tours.splice(tourIndex, 1)

  if (!options.preserveDetailSelection && workspaceStore.selectedTourId === tourClientId) {
    workspaceStore.setSelectedTourId(null)
  }
  if (workspaceStore.hoveredTourId === tourClientId) {
    workspaceStore.setHoveredTourId(null)
  }

  return { tour: removedTour, index: tourIndex }
}

export async function getPinnedWorkspaceClientId(): Promise<string> {
  const userId = useAuthStore().user?.userId
  if (!userId) throw new Error('Sign in to use Pinned')
  if (pinnedWorkspaceCache?.userId === userId) return pinnedWorkspaceCache.clientId

  const pinned = await apiService.getPinnedWorkspaceDetail()
  if (useAuthStore().user?.userId !== userId) {
    throw new Error('Account changed while loading Pinned')
  }

  pinnedWorkspaceCache = { userId, clientId: pinned.clientId }
  return pinned.clientId
}

export function rememberPinnedWorkspaceClientId(clientId: string | null | undefined) {
  const userId = useAuthStore().user?.userId
  if (clientId && userId) pinnedWorkspaceCache = { userId, clientId }
}

const getCachedPinnedWorkspaceClientId = (): string | null => {
  const userId = useAuthStore().user?.userId
  return userId && pinnedWorkspaceCache?.userId === userId ? pinnedWorkspaceCache.clientId : null
}

const isEffectivelyPinned = (
  kind: PinnedMembershipKind,
  clientId: string,
  serverValue: boolean,
  pinnedWorkspaceId: string,
  events: Event[],
): boolean => {
  let isPinned = serverValue

  for (const event of events) {
    const isAdd =
      event.type ===
      (kind === 'tour'
        ? EventType.TOUR_ADDED_TO_WORKSPACE
        : EventType.WORKSPACE_POI_ADDED_TO_WORKSPACE)
    const isRemove =
      event.type ===
      (kind === 'tour'
        ? EventType.TOUR_REMOVED_FROM_WORKSPACE
        : EventType.WORKSPACE_POI_REMOVED_FROM_WORKSPACE)
    if (!isAdd && !isRemove) continue

    const payload = event.payload as unknown as Record<string, unknown>
    const eventClientId = payload.clientId ?? payload.client_id
    const eventWorkspaceId = payload.workspaceClientId ?? payload.workspace_client_id
    if (eventClientId === clientId && eventWorkspaceId === pinnedWorkspaceId) {
      isPinned = isAdd
    }
  }

  return isPinned
}

export async function pinTour(tour: PinnableTour, options: PinTourOptions = {}): Promise<void> {
  const eventStore = useEventStore()
  const workspaceClientId = await getPinnedWorkspaceClientId()
  await eventStore.addEvent({
    type: EventType.TOUR_ADDED_TO_WORKSPACE,
    payload: {
      client_id: tour.clientId,
      workspace_client_id: workspaceClientId,
    },
  })
  setTourPinnedState(tour, true)
  addTourToActivePinnedWorkspace(tour, workspaceClientId)
  if (!options.suppressToast) {
    showSuccess('Pinned')
  }
}

export async function unpinTour(tour: PinnableTour, options: PinTourOptions = {}): Promise<void> {
  const eventStore = useEventStore()
  const wasPinned = tour.isPinned === true
  setTourPinnedState(tour, false)
  let pinnedWorkspaceId = getCachedPinnedWorkspaceClientId()
  let removedState = removeTourFromActivePinnedWorkspace(tour.clientId, pinnedWorkspaceId, options)
  try {
    pinnedWorkspaceId = pinnedWorkspaceId ?? (await getPinnedWorkspaceClientId())
    removedState =
      removedState ?? removeTourFromActivePinnedWorkspace(tour.clientId, pinnedWorkspaceId, options)
    await eventStore.addEvent({
      type: EventType.TOUR_REMOVED_FROM_WORKSPACE,
      payload: {
        client_id: tour.clientId,
        workspace_client_id: pinnedWorkspaceId,
      },
    })
  } catch (error) {
    setTourPinnedState(tour, wasPinned)
    restoreTourToActivePinnedWorkspace(removedState, pinnedWorkspaceId)
    throw error
  }
  if (!options.suppressToast) {
    showSuccess('Unpinned')
  }
}

export async function toggleTourPinned(
  tour: PinnableTour,
  options: PinTourOptions = {},
): Promise<boolean> {
  return runSerializedToggle(`tour:${tour.clientId}`, async () => {
    try {
      if (tour.isPinned) {
        await unpinTour(tour, options)
      } else {
        await pinTour(tour, options)
      }
      return true
    } catch (error) {
      logService.error(LOG_CATEGORIES.API, 'Failed to update pinned tour', { error })
      if (!options.suppressToast) {
        showError(
          `Failed to update Pinned: ${error instanceof Error ? error.message : String(error)}`,
        )
      }
      return false
    }
  })
}

export const setPinnedItemsBatch = async (
  collections: PinnableCollection[],
  shouldPin: boolean,
): Promise<number> => {
  const eventStore = useEventStore()
  return eventStore.runInSyncBatch(async () => {
    const pinnedWorkspaceId = await getPinnedWorkspaceClientId()
    const events: Array<Omit<Event, 'clientId' | 'timestamp'>> = []
    const seenTourIds = new Set<string>()
    const seenPOIIds = new Set<string>()

    for (const collection of collections) {
      for (const tour of collection.tours) {
        if (seenTourIds.has(tour.clientId)) continue
        seenTourIds.add(tour.clientId)
        const isPinned = isEffectivelyPinned(
          'tour',
          tour.clientId,
          tour.isPinned === true,
          pinnedWorkspaceId,
          eventStore.unsyncedEvents,
        )
        if (isPinned === shouldPin) continue

        events.push({
          type: shouldPin
            ? EventType.TOUR_ADDED_TO_WORKSPACE
            : EventType.TOUR_REMOVED_FROM_WORKSPACE,
          payload: {
            client_id: tour.clientId,
            workspace_client_id: pinnedWorkspaceId,
          },
        })
      }

      for (const poi of collection.pois ?? []) {
        if (seenPOIIds.has(poi.clientId)) continue
        seenPOIIds.add(poi.clientId)
        const isPinned = isEffectivelyPinned(
          'poi',
          poi.clientId,
          poi.isPinned === true,
          pinnedWorkspaceId,
          eventStore.unsyncedEvents,
        )
        if (isPinned === shouldPin) continue

        events.push({
          type: shouldPin
            ? EventType.WORKSPACE_POI_ADDED_TO_WORKSPACE
            : EventType.WORKSPACE_POI_REMOVED_FROM_WORKSPACE,
          payload: {
            clientId: poi.clientId,
            workspaceClientId: pinnedWorkspaceId,
          },
        })
      }
    }

    if (events.length > 0) await eventStore.addEvents(events)
    for (const collection of collections) {
      for (const tour of collection.tours) setTourPinnedState(tour, shouldPin)
      for (const poi of collection.pois ?? []) setPOIPinnedState(poi, shouldPin)
    }

    if (!shouldPin && isActivePinnedWorkspace(pinnedWorkspaceId)) {
      const workspaceStore = useWorkspaceStore()
      for (const tourClientId of seenTourIds) {
        removeTourFromActivePinnedWorkspace(tourClientId, pinnedWorkspaceId)
      }
      for (let index = workspaceStore.pois.length - 1; index >= 0; index -= 1) {
        if (seenPOIIds.has(workspaceStore.pois[index].clientId)) {
          workspaceStore.pois.splice(index, 1)
        }
      }
    }

    return events.length
  })
}

export async function pinCollectionItems(collection: PinnableCollection): Promise<void> {
  await setPinnedItemsBatch([collection], true)
}

export async function unpinCollectionItems(collection: PinnableCollection): Promise<number> {
  return setPinnedItemsBatch([collection], false)
}

export async function unpinAllPinnedItems(
  collection: PinnableCollection,
): Promise<UnpinAllPinnedItemsResult> {
  const eventStore = useEventStore()
  const initialTours = [...collection.tours]
  const initialPOIs = [...(collection.pois ?? [])]
  let poisToUnpin: WorkspacePOI[] = []
  const failedTourIds = new Set<string>()
  const failedPOIIds = new Set<string>()

  await eventStore.runInSyncBatch(async () => {
    const pinnedWorkspaceId = await getPinnedWorkspaceClientId()
    poisToUnpin = initialPOIs.filter((poi) => canUnpinPOIFromPinned(poi, pinnedWorkspaceId))

    for (const tour of initialTours) {
      const isPinned = isEffectivelyPinned(
        'tour',
        tour.clientId,
        tour.isPinned === true,
        pinnedWorkspaceId,
        eventStore.unsyncedEvents,
      )
      if (!isPinned) {
        removeTourFromActivePinnedWorkspace(tour.clientId, pinnedWorkspaceId)
        continue
      }

      try {
        await unpinTour(tour, { suppressToast: true })
      } catch (error) {
        failedTourIds.add(tour.clientId)
        logService.error(LOG_CATEGORIES.API, 'Failed to unpin tour while clearing Pinned', {
          tourClientId: tour.clientId,
          error,
        })
      }
    }

    for (const poi of poisToUnpin) {
      const isPinned = isEffectivelyPinned(
        'poi',
        poi.clientId,
        poi.isPinned === true,
        pinnedWorkspaceId,
        eventStore.unsyncedEvents,
      )
      if (!isPinned) {
        if (isActivePinnedWorkspace(pinnedWorkspaceId)) {
          const workspaceStore = useWorkspaceStore()
          const poiIndex = workspaceStore.pois.findIndex(
            (candidate) => candidate.clientId === poi.clientId,
          )
          if (poiIndex !== -1) workspaceStore.pois.splice(poiIndex, 1)
        }
        continue
      }

      try {
        await unpinPOI(poi, { suppressToast: true })
      } catch (error) {
        failedPOIIds.add(poi.clientId)
        logService.error(LOG_CATEGORIES.API, 'Failed to unpin POI while clearing Pinned', {
          poiClientId: poi.clientId,
          error,
        })
      }
    }
  })

  const failedCount = failedTourIds.size + failedPOIIds.size
  const unpinnedCount = initialTours.length + poisToUnpin.length - failedCount

  return { unpinnedCount, failedCount }
}

export async function pinPOI(
  poi: WorkspacePOI,
  options: { suppressToast?: boolean } = {},
): Promise<void> {
  const eventStore = useEventStore()
  const workspaceClientId = await getPinnedWorkspaceClientId()
  await eventStore.addEvent({
    type: EventType.WORKSPACE_POI_ADDED_TO_WORKSPACE,
    payload: {
      clientId: poi.clientId,
      workspaceClientId,
    },
  })
  setPOIPinnedState(poi, true)
  if (!options.suppressToast) {
    showSuccess('Pinned POI')
  }
}

export async function unpinPOI(
  poi: WorkspacePOI,
  options: { suppressToast?: boolean } = {},
): Promise<void> {
  const eventStore = useEventStore()
  const workspaceStore = useWorkspaceStore()
  const pinnedWorkspaceId = await getPinnedWorkspaceClientId()
  await eventStore.addEvent({
    type: EventType.WORKSPACE_POI_REMOVED_FROM_WORKSPACE,
    payload: { clientId: poi.clientId, workspaceClientId: pinnedWorkspaceId },
  })

  setPOIPinnedState(poi, false)
  if (workspaceStore.clientId === pinnedWorkspaceId) {
    const poiIndex = workspaceStore.pois.findIndex(
      (candidate) => candidate.clientId === poi.clientId,
    )
    if (poiIndex !== -1) workspaceStore.pois.splice(poiIndex, 1)
  }
  if (!options.suppressToast) showSuccess('Unpinned POI')
}

export async function togglePOIPinned(
  poi: WorkspacePOI,
  options: { suppressToast?: boolean } = {},
): Promise<boolean> {
  return runSerializedToggle(`poi:${poi.clientId}`, async () => {
    try {
      if (poi.isPinned) {
        await unpinPOI(poi, options)
      } else {
        await pinPOI(poi, options)
      }
      return true
    } catch (error) {
      logService.error(LOG_CATEGORIES.API, 'Failed to update pinned POI', { error })
      if (!options.suppressToast) {
        showError(
          `Failed to update Pinned: ${error instanceof Error ? error.message : String(error)}`,
        )
      }
      return false
    }
  })
}
